import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classes from './CouponFilters.scss'
import { RootState } from 'cityads-v3/types/reducer'

import { useCouponFiltersContext, useEntityLists } from './CouponFilters.hooks'
import { MultiGroupItemList, useChangeHandlers, useFilterToggler } from '../offers/OfferFilters.hooks'

import Button from '@mui/material/Button'
import { MultiGroup } from 'cityads-v3/components/Forms/FilterVertical'
import OfferFilterCheckbox from 'cityads-v3/components/Filter/OfferFilterCheckbox'
import Paper from '@mui/material/Paper'
import FilterCount from '../offers/FilterCount'

export default function CouponFilters(): JSX.Element {
  const { t } = useTranslation(['Offers', 'main'])
  const filterEl = useRef<HTMLElement>(null)
  const isMobile = useSelector((state: RootState) => state.app.isMobile)
  const entityList = useEntityLists()
  const { showAll, handleToggleShowAll } = useFilterToggler()

  const { selectedFilters } = useCouponFiltersContext()
  const {
    handleCheckboxChange,
    handleMultiGroupChange,
  } = useChangeHandlers({
    filterEl,
    submit: selectedFilters.handleSubmit
  })

  return (
    <>
      <Paper
        classes={{ root: classes.paper }}
        elevation={isMobile ? 0 : 2}
        square={isMobile}
      >
        <MultiGroup
          expanded
          topLevelExpanded
          search
          searchLabel={t('filters.region_id_search')}
          name="region_id"
          title={t('filters.region_id')}
          items={entityList.regionList[0] || []}
          onChange={handleMultiGroupChange<number>(selectedFilters.fields.region_id, entityList.regionList[0])}
          value={selectedFilters.fields.region_id.value}
        />

        {showAll ? (
          <>
            <MultiGroup
              expanded
              search
              searchLabel={t('filters.category_id_search')}
              name="category_id"
              title={t('filters.category_id')}
              items={entityList.categoryList[0] || []}
              onChange={handleMultiGroupChange(selectedFilters.fields.category, entityList.categoryList[0] as unknown as MultiGroupItemList<number>)}
              value={selectedFilters.fields.category.value}
            />

            <MultiGroup
              search
              searchLabel={t('main:search')}
              name="offers"
              title={t('filters.offers')}
              items={entityList.couponOfferList || []}
              sliceItems={15}
              onChange={handleMultiGroupChange(selectedFilters.fields.offers, entityList.couponOfferList)}
              value={selectedFilters.fields.offers.value}
            />

            <MultiGroup
              search
              searchLabel={t('main:search')}
              name="coupon_type"
              title={t('filters.couponType')}
              items={entityList.couponTypeList || []}
              onChange={handleMultiGroupChange(selectedFilters.fields.type, entityList.couponTypeList)}
              value={selectedFilters.fields.type.value}
            />

            <MultiGroup
              search
              searchLabel={t('main:search')}
              name="coupon_status"
              title={t('filters.couponStatus')}
              items={entityList.couponStatusList || []}
              onChange={handleMultiGroupChange(selectedFilters.fields.status, entityList.couponStatusList)}
              value={selectedFilters.fields.status.value}
            />

            <MultiGroup
              search
              searchLabel={t('main:search')}
              name="coupon_language"
              title={t('filters.couponLanguage')}
              items={entityList.couponLanguageList || []}
              onChange={handleMultiGroupChange(selectedFilters.fields.language_id, entityList.couponLanguageList)}
              value={selectedFilters.fields.language_id.value}
            />

            {entityList.couponSectoringList?.length ? (
              <MultiGroup
                search
                searchLabel={t('main:search')}
                name="coupon_sectoring"
                title={t('filters.couponSectoring')}
                items={entityList.couponSectoringList || []}
                onChange={handleMultiGroupChange(selectedFilters.fields.sectoring, entityList.couponSectoringList)}
                value={selectedFilters.fields.sectoring.value}
              />
            ) : null}

            <MultiGroup
              search
              searchLabel={t('main:search')}
              name="coupon_scopes"
              title={t('filters.couponScopes')}
              items={entityList.couponScopeList || []}
              onChange={handleMultiGroupChange(selectedFilters.fields.scopes, entityList.couponScopeList)}
              value={selectedFilters.fields.scopes.value}
            />

            <OfferFilterCheckbox
              title={t('filters.isExclusive')}
              checked={selectedFilters.fields.is_exclusive.value}
              onToggle={handleCheckboxChange(selectedFilters.fields.is_exclusive)}
            />

            <OfferFilterCheckbox
              title={t('filters.isPersonal')}
              checked={selectedFilters.fields.is_personal.value}
              onToggle={handleCheckboxChange(selectedFilters.fields.is_personal)}
            />
          </>
        ) : null}

        <div className={classes.actionButtons}>
          <Button
            color="primary"
            onClick={selectedFilters.handleSubmit}
            variant="contained"
          >
            {t('main:buttons.apply')}
          </Button>

          {isMobile ? (
            <Button
              onClick={handleToggleShowAll}
            >
              {showAll ? t('hideFilters') : t('showFilters')}
            </Button>
          ) : null}
        </div>
      </Paper>

      <FilterCount
        filterEl={filterEl}
        onApply={selectedFilters.handleSubmit}
      />
    </>
  )
}
